import { forwardRef } from "react";
import UIkit from "uikit";
import { WORK_ORDER_CATEGORY_OPTIONS } from "../categoryOptions";
import { TASK_TYPE_OPTIONS } from "../taskTypeOptions";
import SearchableDropdown from "../../common/SearchableDropdown";

const FilterPanel = forwardRef(({
  filterDraft,
  setFilterDraft,
  setCriteria,
  preloading,
  tagValues,
  assignees,
  propertyValues,
  showTaskType = false,
}, ref) => {
  
  const handleApplyFilters = () => {
    setCriteria((prev) => ({
      ...prev,
      ...filterDraft,
      page: 0,
    }));

    if (ref.current) {
      const dropdown = UIkit.dropdown(ref.current);
      dropdown.hide(false); // false = no animation cancel
    }
  };

  const handleClearFilters = () => {
    setFilterDraft({
      priority: "",
      ...(showTaskType ? { task_type: "" } : {}),
      category: "",
      tag: "",
      assignee: "",
      property: "",
      date_from: "",
      date_to: "",
    });
  };

  return (
    <div className="hms-filters-btn">
      <button className="filter" type="button">
        <img src="/images/filter.svg" uk-tooltip="Filter" />
        <span uk-icon="icon: filter; ratio: 0.9"></span>
      </button>

      <div
        ref={ref}
        uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400"
        className="hms-filters-dropdown"
      >
        <div className="uk-form-stacked">
          {/* Priority */}
          <div className="uk-margin-small">
            <label className="uk-form-label">Priority</label>
            <div className="uk-form-controls">
              <select
                className="uk-select"
                value={filterDraft.priority}
                onChange={(e) =>
                  setFilterDraft((prev) => ({ ...prev, priority: e.target.value }))
                }
              >
                <option value="">All Priorities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          {showTaskType && (
            <div className="uk-margin-small">
              <label className="uk-form-label">Task Type</label>
              <div className="uk-form-controls">
                <select
                  className="uk-select"
                  value={filterDraft.task_type || ""}
                  onChange={(e) =>
                    setFilterDraft((prev) => ({ ...prev, task_type: e.target.value }))
                  }
                >
                  <option value="">All Task Types</option>
                  {TASK_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Category */}
          <div className="uk-margin-small">
            <label className="uk-form-label">Category</label>
            <div className="uk-form-controls">
              <select
                className="uk-select"
                value={filterDraft.category}
                onChange={(e) =>
                  setFilterDraft((prev) => ({ ...prev, category: e.target.value }))
                }
              >
                <option value="">All Categories</option>
                {WORK_ORDER_CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tag */}
          {!preloading && (
            <div className="uk-margin-small">
              <label className="uk-form-label">Tag</label>
              <div className="uk-form-controls">
                <SearchableDropdown
                  inline
                  value={filterDraft.tag}
                  onChange={(val) =>
                    setFilterDraft((prev) => ({ ...prev, tag: val }))
                  }
                  placeholder="All Tags"
                  options={[
                    { value: "", label: "All Tags" },
                    ...tagValues.map((tag) => ({
                      value: tag.id,
                      label: tag.name,
                    })),
                  ]}
                />
              </div>
            </div>
          )}

          {/* Assignee */}
          {!preloading && (
            <div className="uk-margin-small">
              <label className="uk-form-label">Assignee</label>
              <div className="uk-form-controls">
                <select
                  className="uk-select"
                  value={filterDraft.assignee}
                  onChange={(e) =>
                    setFilterDraft((prev) => ({ ...prev, assignee: e.target.value }))
                  }
                >
                  <option value="">All Assignees</option>
                  {assignees.map((assignee) => (
                    <option
                      key={`assignee_${assignee.value}`}
                      value={assignee.value}
                    >
                      {assignee.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Property */}
          {!preloading && (
            <div className="uk-margin-small">
              <label className="uk-form-label">Property</label>
              <div className="uk-form-controls">
                <SearchableDropdown
                  inline
                  value={filterDraft.property}
                  onChange={(val) =>
                    setFilterDraft((prev) => ({ ...prev, property: val }))
                  }
                  placeholder="All Properties"
                  options={[
                    { value: "", label: "All Properties" },
                    ...propertyValues.map((property) => ({
                      value: property.rental_id,
                      label: property.pms_id,
                    })),
                  ]}
                />
              </div>
            </div>
          )}

          {/* Date Range */}
          <div className="uk-margin-small">
            <label className="uk-form-label">Date Range</label>
            <div className="uk-form-controls">
              <div className="uk-flex uk-flex-middle uk-flex-wrap">
                <input
                  className="uk-input uk-margin-small-right"
                  style={{ maxWidth: 150 }}
                  type="date"
                  value={filterDraft.date_from}
                  onChange={(e) =>
                    setFilterDraft((prev) => ({ ...prev, date_from: e.target.value }))
                  }
                />
                <span className="uk-margin-small-right">to</span>
                <input
                  className="uk-input"
                  style={{ maxWidth: 150 }}
                  type="date"
                  value={filterDraft.date_to}
                  onChange={(e) =>
                    setFilterDraft((prev) => ({ ...prev, date_to: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>

          <div className="hms-filter-actions uk-margin-small-top">
            <button
              type="button"
              className="uk-button hms-btn hms-red-btn"
              onClick={handleApplyFilters}
            >
              Apply
            </button>
            <button
              type="button"
              className="uk-button hms-btn hms-white-btn"
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

FilterPanel.displayName = 'FilterPanel';

export default FilterPanel;
